import { useEffect, useRef } from "react";
import usePersistedBoundStore from "@/store/persisted-bound-store";
import { getTabEnvironment, saveTabEnvironment } from "@/utils/tab-session";
import type { Environment } from "@/repositories/process/types";

/**
 * Hook that manages per-tab environment persistence.
 *
 * This hook solves the problem of multiple tabs sharing localStorage:
 * - When a user opens Process A in Tab 1 and Process B in Tab 2,
 *   without this hook, both tabs would show Process B's data
 * - With this hook, each tab maintains its own environment in sessionStorage
 *
 * Usage: Call this hook in a layout or provider component that wraps the app.
 *
 * Behavior:
 * 1. On mount (after hydration), checks if there's tab-specific environment data
 * 2. If found, restores that data to the store (overriding localStorage data)
 * 3. Provides a function to save environment that persists to both sessionStorage and store
 */
export default function useTabEnvironment() {
  const hasHydrated = usePersistedBoundStore.use.hasHydrated();
  const setEnvironment = usePersistedBoundStore.use.setEnvironment();
  const setEnvironmentUrl = usePersistedBoundStore.use.setEnvironmentUrl();
  const hasRestoredRef = useRef(false);

  // Restore tab-specific environment after store hydration
  useEffect(() => {
    if (!hasHydrated || hasRestoredRef.current) {
      return;
    }

    const tabEnvData = getTabEnvironment();
    if (tabEnvData) {
      // Restore this tab's specific environment data
      setEnvironment(tabEnvData.environment);
      setEnvironmentUrl(tabEnvData.environmentUrl);
    }

    hasRestoredRef.current = true;
  }, [hasHydrated, setEnvironment, setEnvironmentUrl]);
}

/**
 * Saves environment data to both the store and sessionStorage.
 * Use this instead of directly calling setEnvironment/setEnvironmentUrl
 * when you want to maintain per-tab persistence.
 */
export function useSetTabEnvironment() {
  const setEnvironment = usePersistedBoundStore.use.setEnvironment();
  const setEnvironmentUrl = usePersistedBoundStore.use.setEnvironmentUrl();

  return (environment: Environment, environmentUrl: string) => {
    // Save to sessionStorage for tab-specific persistence
    saveTabEnvironment(environment, environmentUrl);

    // Also update the store (which will persist to localStorage)
    setEnvironment(environment);
    setEnvironmentUrl(environmentUrl);
  };
}
